import React, { useContext } from 'react';
import Button from '../Button';
import { getServicesData } from '../data';
import { LanguageContext } from '../LanguageContext';

const DetailList: React.FC<{ items: string[] }> = ({ items }) => {
    if (!items || !Array.isArray(items)) return null;
    return (
        <ul className="space-y-2">
            {items.map((item: string, itemIndex: number) => (
                <li key={item || itemIndex} className="flex items-start">
                    <svg className="flex-shrink-0 h-5 w-5 text-accent me-2 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-600 dark:text-gray-400">{item || ''}</span>
                </li>
            ))}
        </ul>
    );
};

const DetailCard: React.FC<{ title: string; items: string[] }> = ({ title, items }) => (
    <div>
        <h4 className="text-sm font-semibold tracking-wider uppercase text-gray-500 dark:text-gray-400 mb-4">{title}</h4>
        <DetailList items={items} />
    </div>
);


const Services: React.FC<{ openChat: (source?: string) => void }> = ({ openChat }) => {
    const { t } = useContext(LanguageContext);
    const servicesData = getServicesData(t);

    return (
        <section id="services" className="py-20 sm:py-32 bg-white dark:bg-black scroll-mt-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center">
                    <h2 className="text-3xl font-bold tracking-tight sm:text-4xl text-gray-900 dark:text-gray-50">{t?.services?.title || ''}</h2>
                    <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">{t?.services?.description || ''}</p>
                </div>

                <div className="mt-12 space-y-5">
                    {servicesData.map((service: any, index: number) => (
                        <details
                            id={`service-${service?.id || index}`}
                            key={service?.id || service?.pillar || index}
                            className="group scroll-mt-28 border border-gray-200 dark:border-gray-800 rounded-xl bg-gray-50 dark:bg-gray-900/50 open:shadow-lg open:border-accent/60 transition-all duration-300"
                        >
                            <summary className="list-none cursor-pointer p-6 sm:p-8 flex items-start justify-between gap-5 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 dark:focus-visible:ring-offset-black [&::-webkit-details-marker]:hidden">
                                <div>
                                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-50">{service?.pillar || ''}</h3>
                                    <p className="mt-2 text-gray-600 dark:text-gray-400 max-w-4xl">{service?.description || ''}</p>
                                </div>
                                <span className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 group-open:bg-accent group-open:text-accent-foreground group-open:border-accent transition-colors" aria-hidden="true">
                                    <svg className="h-5 w-5 transition-transform duration-300 group-open:rotate-45" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M10 4v12M4 10h12" strokeLinecap="round" />
                                    </svg>
                                </span>
                            </summary>
                            <div className="px-6 pb-8 sm:px-8 sm:pb-10">
                                <div className="border-t border-gray-200 dark:border-gray-800 pt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
                                    <DetailCard title={t?.services?.areasTitle || ''} items={service?.areas} />
                                    <DetailCard title={t?.services?.deliverablesTitle || ''} items={service?.deliverables} />
                                    <DetailCard title={t?.services?.techMethodsTitle || ''} items={service?.techMethods} />
                                    <DetailCard title={t?.services?.useCasesTitle || ''} items={service?.useCases} />
                                </div>
                            </div>
                        </details>
                    ))}
                </div>

                <div className="mt-20 text-center">
                    <Button onClick={() => openChat('services_cta')} variant="primary">{t?.assistant?.title || 'AI Assistant'}</Button>
                </div>
            </div>
        </section>
    );
};

export default Services;
